'use client'

import type { ComponentProps, ReactNode } from 'react'

import { LinkButton } from '@/components/ui/button'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'

type LinkButtonVariant = ComponentProps<typeof LinkButton>['variant']

interface HeroProofPoint {
  label: string
  value: string
}

interface HeroSecondaryCta {
  text: string
  href: string
  variant?: LinkButtonVariant
}

interface HeroProps {
  title: ReactNode
  subtitle?: ReactNode
  description: ReactNode
  ctaText: string
  ctaHref: string
  className?: string
  eyebrow?: string
  /** Optional product mark/lockup rendered centered above the eyebrow. */
  mark?: ReactNode
  /**
   * Headline face. Defaults to `hero-title` (the biggest hero scale). Product
   * pages pass `thesis-headline thesis-headline--category` to match the
   * /products family headline size. The h1's own margin/centering live on the
   * wrapper below because `.thesis-headline` sets `margin: 0` (unlayered) and
   * would kill utilities placed on the h1 itself.
   */
  titleClassName?: string
  secondaryCta?: HeroSecondaryCta
  proofPoints?: HeroProofPoint[]
}

export default function Hero({
  title,
  subtitle,
  description,
  ctaText,
  ctaHref,
  className = '',
  eyebrow,
  mark,
  titleClassName = 'hero-title',
  secondaryCta,
  proofPoints = [],
}: HeroProps) {
  return (
    <section className={`text-center mb-20 mt-16 md:mt-20 ${className}`}>
      <LenisFadeIn delay={100}>
        <div className='text-center'>
          {mark && <div className='flex justify-center mb-6'>{mark}</div>}
          {eyebrow && (
            <p className='text-sm font-medium mb-4 uppercase tracking-wider text-muted-foreground'>
              {eyebrow}
            </p>
          )}
          <div className='max-w-6xl mx-auto mb-6 md:mb-8'>
            <h1 className={titleClassName} data-hero-title=''>
              {title}
            </h1>
          </div>
          {subtitle && (
            <h2 className='text-2xl md:text-4xl lg:text-5xl font-semibold text-growth max-w-4xl mx-auto mb-10 md:mb-12'>
              {subtitle}
            </h2>
          )}
        </div>
      </LenisFadeIn>
      <LenisFadeIn delay={200}>
        <p
          className='lead max-w-4xl mx-auto mb-10 md:mb-12'
          data-hero-description=''
        >
          {description}
        </p>
      </LenisFadeIn>
      <LenisFadeIn delay={300}>
        <div className='flex flex-col sm:flex-row gap-4 justify-center mt-12'>
          {ctaHref === '#open-chat' ? (
            <button
              onClick={() => {
                // Dispatch custom event to open chat widget in expanded mode
                window.dispatchEvent(
                  new CustomEvent('openChatWidget', {
                    detail: { expanded: true },
                  })
                )
              }}
              className='group relative inline-flex items-center justify-center gap-2 px-10 h-12 text-base font-semibold text-white bg-[var(--cta-fill)] rounded-[2px] before:absolute before:bottom-0 before:left-0 before:h-[3px] before:w-full before:bg-[#FDC304] before:transition-all before:duration-150 hover:translate-y-[-3px] hover:shadow-[0_6px_20px_rgba(249,11,138,0.3)] active:translate-y-[-1px] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              <span className='flex items-center gap-2'>
                {ctaText}
                <svg
                  className='w-5 h-5 transition-transform duration-150 group-hover:translate-x-1'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M13 7l5 5m0 0l-5 5m5-5H6'
                  />
                </svg>
              </span>
            </button>
          ) : (
            <LinkButton
              href={ctaHref}
              variant='action'
              size='lg'
              className='group'
            >
              <span className='flex items-center gap-2'>
                {ctaText}
                <svg
                  className='w-5 h-5 transition-transform duration-150 group-hover:translate-x-1'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M13 7l5 5m0 0l-5 5m5-5H6'
                  />
                </svg>
              </span>
            </LinkButton>
          )}
          {secondaryCta && (
            <LinkButton
              href={secondaryCta.href}
              variant={secondaryCta.variant ?? 'outline'}
              size='lg'
            >
              {secondaryCta.text}
            </LinkButton>
          )}
        </div>
      </LenisFadeIn>
      {proofPoints.length > 0 && (
        <LenisStaggered delay={500} staggerDelay={100}>
          <dl className='mt-14 grid gap-6 text-left sm:grid-cols-3 max-w-5xl mx-auto'>
            {proofPoints.map(point => (
              <div
                key={`${point.label}-${point.value}`}
                className='rounded-2xl bg-card/70 px-6 py-5 border border-line-strong backdrop-blur-sm'
              >
                <dd className='text-2xl font-semibold text-foreground'>
                  {point.value}
                </dd>
                <dt className='mt-2 text-sm uppercase tracking-wide text-muted-foreground'>
                  {point.label}
                </dt>
              </div>
            ))}
          </dl>
        </LenisStaggered>
      )}
    </section>
  )
}
